import type { DocumentFile, UnifiedAIResponse } from '@quipex/shared/contracts';
import { v4 as uuidv4 } from 'uuid';
import type { QuippyDocumentOption, QuippySource } from './models';

const AMBIGUOUS_DOCUMENT_PROMPT =
    'You could be referring to multiple documents. Please select one to generate an answer from:';

/**
 * Maps API DocumentFile objects to the slice-local QuippySource view model.
 */
export function toQuippySources(files: DocumentFile[]): QuippySource[] {
    return files.map((f) => ({
        id: f.id,
        title: f.title,
        url: f.url,
        source: f.source,
        ...toBuildingMetadata(f),
    }));
}

export function toQuippyResponseContent(response: UnifiedAIResponse): string {
    const documentOptions = toQuippyDocumentOptions(response);
    if (documentOptions.length === 0) return response.response;

    return (
        toAmbiguousDocumentPrompt(response, documentOptions) ||
        AMBIGUOUS_DOCUMENT_PROMPT
    );
}

export function toQuippyDocumentOptions(
    response: UnifiedAIResponse
): QuippyDocumentOption[] {
    const clarification = response.clarification;
    if (clarification?.reason !== 'ambiguous-documents') {
        return [];
    }

    if (clarification.options.length > 0) {
        return clarification.options.map((option) => ({
            fileId: option.fileId,
            title: option.title,
            url: option.url,
            ...toBuildingMetadata(option),
            pageNumbers: option.pageNumbers,
            excerpt: option.excerpt,
        }));
    }

    return (response.files ?? []).flatMap((file) =>
        file.id === null
            ? []
            : [
                  {
                      fileId: file.id,
                      title: file.title,
                      url: file.url,
                      ...toBuildingMetadata(file),
                      pageNumbers:
                          file.pageNumber == null
                              ? undefined
                              : [file.pageNumber],
                      excerpt: file.highlightedText,
                  },
              ]
    );
}

/**
 * Generates a collision-resistant ID for a chat message.
 */
export function generateMessageId(): string {
    return uuidv4();
}

/**
 * Generates a collision-resistant ID for a chat thread.
 */
export function generateChatId(): string {
    return uuidv4();
}

function toAmbiguousDocumentPrompt(
    response: UnifiedAIResponse,
    documentOptions: QuippyDocumentOption[]
): string {
    const prompt = response.clarification?.prompt?.trim();
    if (!prompt) return '';

    const optionTitles = new Set(
        documentOptions
            .flatMap((option) => [
                option.title.trim(),
                toDocumentDisplayLabel(option).trim(),
            ])
            .filter(Boolean)
    );

    return prompt
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => {
            const lineWithoutListMarker = line
                .replace(/^(?:[-*]|\d+[.)])\s+/, '')
                .trim();

            return !optionTitles.has(lineWithoutListMarker);
        })
        .join('\n')
        .trim();
}

function toBuildingMetadata(source: {
    buildingId?: string;
    buildingName?: string;
}): Pick<QuippyDocumentOption, 'buildingId' | 'buildingName'> {
    return {
        ...(source.buildingId ? { buildingId: source.buildingId } : {}),
        ...(source.buildingName ? { buildingName: source.buildingName } : {}),
    };
}

function toDocumentDisplayLabel(
    document: Pick<QuippyDocumentOption, 'buildingName' | 'title'>
): string {
    const title = document.title.trim();
    const buildingName = document.buildingName?.trim();
    return buildingName ? `${title} - ${buildingName}` : title;
}
